import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import dayjs from "dayjs";
import axios from "axios";
import CustomDatePicker from "../../resuableComponents/CustomDatePicker";
import "./MedicalCertificateModal.css";
import headerImage from "../../../Images/header-logo.png";
import footerImage from "../../../Images/footer-logo.png";
import { toast } from "react-toastify";

const MedicalCertificateModal = ({ open, onClose, patient }) => {
  const [form, setForm] = useState({
    fromDate: null,
    toDate: null,
  });
  const [consultations, setConsultations] = useState([]);
  const [loadingConsultations, setLoadingConsultations] = useState(false);
  const [errors, setErrors] = useState({
    fromDate: "",
    toDate: "",
  });
  const [touched, setTouched] = useState({
    fromDate: false,
    toDate: false,
  });

  const authData = JSON.parse(localStorage.getItem("authData"));

  // Fetch consultations when modal opens
  useEffect(() => {
    if (open && patient?._id) {
      fetchConsultations();
      setForm({
        fromDate: null,
        toDate: null,
      });
      setErrors({
        fromDate: "",
        toDate: "",
      });
      setTouched({
        fromDate: false,
        toDate: false,
      });
    }
  }, [open, patient]);

  const fetchConsultations = async () => {
    if (!patient?._id) {
      console.warn("No patient ID found");
      return;
    }

    if (!authData?.accessToken) {
      console.error("Authentication required");
      return;
    }

    try {
      setLoadingConsultations(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}patient-history?patientId=${patient._id}`,
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const consultationsData = response.data.history
        .filter((item) => item.consultation)
        .map((item) => ({
          ...item.consultation,
          appointmentDate: item.appointmentDate,
          doctorName: item.doctor?.name || "N/A",
        }));

      setConsultations(consultationsData);
    } catch (err) {
      console.error("Failed to fetch consultations:", err);
      toast.error("Failed to load consultation data");
    } finally {
      setLoadingConsultations(false);
    }
  };

  // Revalidate toDate when fromDate changes
  useEffect(() => {
    if (touched.toDate && form.toDate && form.fromDate) {
      const error = validateField("toDate", form.toDate);
      setErrors((prev) => ({ ...prev, toDate: error }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fromDate, form.toDate, touched.toDate]);

  // Do not render if closed or no patient yet
  if (!open || !patient) return null;

  const validateField = (name, value, currentForm = form) => {
    let error = "";

    switch (name) {
      case "fromDate":
        if (!value) {
          error = "From date is required";
        }
        break;
      case "toDate":
        if (!value) {
          error = "To date is required";
        } else if (
          currentForm.fromDate &&
          dayjs.isDayjs(value) &&
          dayjs.isDayjs(currentForm.fromDate)
        ) {
          if (value.isBefore(currentForm.fromDate, "day")) {
            error = "To date must be after or equal to from date";
          }
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const value = form[name];
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleDateChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      fromDate: validateField("fromDate", form.fromDate),
      toDate: validateField("toDate", form.toDate),
    };

    setErrors(newErrors);
    setTouched({
      fromDate: true,
      toDate: true,
    });

    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleGenerate = (e) => {
    e.preventDefault();

    // Validate all fields before generating PDF
    if (!validateForm()) {
      return;
    }

    if (consultations.length === 0) {
      toast.error("No consultation data available");
      return;
    }

    // Get latest consultation for diagnosis/complaints
    const latestConsultation = consultations[consultations.length - 1];

    // Get all unique treatments from consultations
    const allTreatments = [];

    // Helper function to parse date
    const parseAppointmentDate = (dateStr) => {
      if (!dateStr) return null;
      // Try different date formats
      const datePart = dateStr.split(" ")[0]; // Get date part before time
      // Try DD-MM-YYYY format first
      let parsed = dayjs(datePart, "DD-MM-YYYY", true);
      if (!parsed.isValid()) {
        // Try other formats
        parsed = dayjs(datePart);
      }
      return parsed.isValid() ? parsed : null;
    };

    // Collect treatments from all consultations
    consultations.forEach((c) => {
      if (c.treatments && Array.isArray(c.treatments)) {
        c.treatments.forEach((t) => {
          // Handle both string and object formats (treatments are objects with name property)
          const treatmentName = typeof t === "string" ? t : t.name || String(t);
          if (
            treatmentName &&
            treatmentName.trim() &&
            !allTreatments.includes(treatmentName)
          ) {
            allTreatments.push(treatmentName);
          }
        });
      }
    });

    // If dates are selected, try to filter treatments by date range (optional enhancement)
    // But always show all treatments if date filtering doesn't work
    if (form.fromDate && form.toDate && allTreatments.length > 0) {
      // Optional: Could filter by date range here if needed
      // For now, we show all treatments regardless of date range
    }

    // Extract diagnosis or complaints for "suffering from"
    const sufferingFrom =
      latestConsultation.diagnosis ||
      latestConsultation.complaints ||
      "treatment";

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const headerHeight = 25;
    const footerHeight = 25;
    let yPos = headerHeight + 20;

    // ===== HEADER & FOOTER =====
    if (headerImage) {
      doc.addImage(headerImage, "PNG", 0, 0, pageWidth, headerHeight);
    }
    if (footerImage) {
      doc.addImage(
        footerImage,
        "PNG",
        0,
        pageHeight - footerHeight,
        pageWidth,
        footerHeight
      );
    }

    // ===== TITLE =====
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    const hospitalTitle = "ASHTANG AYURVED HOSPITAL";
    const titleWidth = doc.getTextWidth(hospitalTitle);
    doc.text(hospitalTitle, (pageWidth - titleWidth) / 2, yPos);
    yPos += 12;

    // Box: MEDICAL CERTIFICATE
    const boxWidth = 70;
    const boxHeight = 10;
    const boxX = (pageWidth - boxWidth) / 2;
    doc.rect(boxX, yPos, boxWidth, boxHeight);
    doc.setFontSize(12);
    const certText = "MEDICAL CERTIFICATE";
    const certTextWidth = doc.getTextWidth(certText);
    doc.text(certText, (pageWidth - certTextWidth) / 2, yPos + 6.5);
    yPos += 18;

    // ===== MAIN BODY =====
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    const centerX = pageWidth / 2;
    doc.text("To whomever it may concern,", centerX, yPos, { align: "center" });
    yPos += 12;

    const name = patient.name || "";
    const gender = (patient.gender || "").toLowerCase();
    // const pronoun = gender.startsWith("f") ? "She" : "He";
    let pronoun;
    if (gender.startsWith("f")) {
      pronoun = "She";
    } else if (gender.startsWith("m")) {
      pronoun = "He";
    } else {
      pronoun = "They";
    }

    const fromDateStr = form.fromDate
      ? (dayjs.isDayjs(form.fromDate)
          ? form.fromDate
          : dayjs(form.fromDate)
        ).format("DD/MM/YYYY")
      : "____";
    const toDateStr = form.toDate
      ? (dayjs.isDayjs(form.toDate) ? form.toDate : dayjs(form.toDate)).format(
          "DD/MM/YYYY"
        )
      : "____";
    const treatmentsList = allTreatments;

    const wrap = (text) => doc.splitTextToSize(text, pageWidth - margin * 2);

    // 1) This is to certify...
    const para1 = `This is to certify that   ${name}  is advised for panchakarma and physiotherapy treatment under medical observation.`;
    wrap(para1).forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 6;
    });
    yPos += 2;

    // 2) Suffering from
    const para2 = `${pronoun} is suffering from  ${sufferingFrom}.`;
    wrap(para2).forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 6;
    });
    yPos += 2;

    // 3) Under treatment & rest
    const para3 = `${pronoun} is under treatment and is advised to take rest.`;
    wrap(para3).forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 6;
    });
    yPos += 2;

    // 4) From–to–for
    const para4 = `${pronoun} was under treatment from  ${fromDateStr}  to  ${toDateStr}  for ${sufferingFrom}.`;
    wrap(para4).forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 6;
    });
    yPos += 10;

    // ===== FOLLOWING TREATMENT LIST =====
    if (treatmentsList && treatmentsList.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Following treatment was done :-", margin, yPos);
      yPos += 8;

      doc.setFont("helvetica", "normal");

      const leftX = margin;
      const rightX = pageWidth / 2 + 5;
      let leftY = yPos;
      let rightY = yPos;

      treatmentsList.forEach((t, index) => {
        const treatmentText = typeof t === "string" ? t : t.name || String(t);
        const label = `${index + 1}) ${treatmentText}`;
        const isLeft = index % 2 === 0;
        if (isLeft) {
          doc.text(label, leftX, leftY);
          leftY += 6;
        } else {
          doc.text(label, rightX, rightY);
          rightY += 6;
        }
      });
    }

    // ===== DATE & SIGNATURE =====
    // const bottomY = pageHeight - footerHeight - 25;
    // const today = dayjs().format("DD/MM/YYYY");

    // doc.setFontSize(10);
    // doc.text(`Date: ${today}`, margin, bottomY);

    // const signX = pageWidth - margin - 50;
    // doc.text("Doctor's Signature", signX, bottomY);
    // doc.line(signX, bottomY + 2, signX + 45, bottomY + 2);

    const safeName = (name || "Patient").replace(/\s+/g, "_");
    doc.save(`Medical_Certificate_${safeName}.pdf`);

    toast.success("Medical certificate generated successfully!");
    onClose();
  };

  // Extract data from consultations for display
  const latestConsultation =
    consultations.length > 0 ? consultations[consultations.length - 1] : null;
  const sufferingFrom =
    latestConsultation?.diagnosis || latestConsultation?.complaints || "N/A";

  // Get all unique treatments from consultations
  const allTreatmentsFromAPI = [];
  consultations.forEach((c) => {
    if (c.treatments && Array.isArray(c.treatments)) {
      c.treatments.forEach((t) => {
        const treatmentName = typeof t === "string" ? t : t.name;
        if (treatmentName && !allTreatmentsFromAPI.includes(treatmentName)) {
          allTreatmentsFromAPI.push(treatmentName);
        }
      });
    }
  });

  return (
    <div className="med-cert-modal-overlay">
      <div className="med-cert-modal">
        <h3>Create medical certificate</h3>
        {loadingConsultations ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            Loading consultation data...
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="med-cert-form">
            {consultations.length > 0 && (
              <div className="med-cert-source">
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  Data from consultations:
                </p>
                <p style={{ margin: "0 0 4px 0", fontSize: "0.85rem" }}>
                  <strong>Suffering from:</strong> {sufferingFrom}
                </p>
                <p style={{ margin: "0 0 4px 0", fontSize: "0.85rem" }}>
                  <strong>Treatments:</strong>{" "}
                  {allTreatmentsFromAPI.length > 0
                    ? allTreatmentsFromAPI.join(", ")
                    : "None"}
                </p>
              </div>
            )}

            <div className="med-cert-row">
              <div className="med-cert-field">
                <label>
                  From date <span className="med-cert-required">*</span>
                </label>
                <div
                  onBlur={() => handleBlur("fromDate")}
                  className={errors.fromDate ? "med-cert-datepicker-error" : ""}
                >
                  <CustomDatePicker
                    label="Select From Date"
                    value={form.fromDate}
                    onChange={(value) => handleDateChange("fromDate", value)}
                    minDate={null}
                    error={!!errors.fromDate}
                  />
                </div>
                {errors.fromDate && (
                  <span className="med-cert-error-message">{errors.fromDate}</span>
                )}
              </div>
              <div className="med-cert-field">
                <label>
                  To date <span className="med-cert-required">*</span>
                </label>
                <div
                  onBlur={() => handleBlur("toDate")}
                  className={errors.toDate ? "med-cert-datepicker-error" : ""}
                >
                  <CustomDatePicker
                    label="Select To Date"
                    value={form.toDate}
                    onChange={(value) => handleDateChange("toDate", value)}
                    minDate={form.fromDate || null}
                    error={!!errors.toDate}
                  />
                </div>
                {errors.toDate && (
                  <span className="med-cert-error-message">{errors.toDate}</span>
                )}
              </div>
            </div>

            <div className="med-cert-actions">
              <button
                type="button"
                className="med-cert-btn med-cert-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="med-cert-btn med-cert-btn-primary"
                disabled={
                  !form.fromDate || !form.toDate || consultations.length === 0
                }
              >
                Create medical certificate
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MedicalCertificateModal;
